/**
 * Favourite files (§10).
 *
 * The recent list answers "open it again"; favourites answer "open the one I
 * keep coming back to". The difference is deliberateness: a recent entry is
 * recorded for you and ages out, a favourite is pinned by you and stays until
 * you unpin it. So the two are separate stores rather than a flag on one — a
 * favourite must survive the recent list scrolling it off, and clearing recents
 * must not touch what someone chose to keep.
 *
 * The rules that make the recent list safe apply here unchanged, because a
 * favourites list is still a list of what someone has been reading, kept on
 * their device:
 *
 * - **A file whose contents may be credentials is never pinned.** §29 excludes
 *   sensitive files from lists; an explicit pin does not override that, because
 *   the leak is the *name* sitting in storage, not the intent behind it.
 * - **A handle is only kept when the host can reopen it.** In a browser a
 *   handle dies with the tab, so the star is not offered there at all — §19,
 *   an affordance that cannot be honoured should not exist.
 * - **The entry is verified before it is shown**, so a favourite whose file has
 *   moved disappears rather than failing on click.
 */
import type { FileStore } from '@moduviewer/core';

const KEY = 'modu.favourites.v1';

/**
 * A ceiling, not a policy. Favourites are deliberate, so there is no eviction —
 * but unbounded growth in localStorage is a bug waiting to happen, so a pin
 * past this many is refused rather than silently dropping an older one.
 */
const MAX_ENTRIES = 200;

export interface FavouriteFile {
  readonly handle: string;
  readonly displayName: string;
  readonly formatId: string | null;
  readonly sizeBytes: number;
  readonly addedAtMs: number;
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function isFavourite_(value: unknown): value is FavouriteFile {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v['handle'] === 'string'
    && typeof v['displayName'] === 'string'
    && (typeof v['formatId'] === 'string' || v['formatId'] === null)
    && typeof v['sizeBytes'] === 'number'
    && typeof v['addedAtMs'] === 'number';
}

function readAll(): FavouriteFile[] {
  const store = storage();
  if (!store) return [];
  try {
    const parsed: unknown = JSON.parse(store.getItem(KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter(isFavourite_) : [];
  } catch {
    return [];
  }
}

function writeAll(entries: readonly FavouriteFile[]): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch { /* a full quota is not worth interrupting an action for */ }
}

export interface FavouriteOptions {
  readonly handle: string;
  readonly displayName: string;
  readonly formatId: string | null;
  readonly sizeBytes: number;
  /** §29 — set by detection when the file may hold credentials. */
  readonly sensitive: boolean;
  /** False in a browser, where a handle cannot outlive the tab. */
  readonly reopenable: boolean;
}

/**
 * Pin a file. Returns whether it is now pinned.
 *
 * Idempotent: pinning an already-pinned file leaves it in place rather than
 * moving it, because a favourites list a person curates should not reorder
 * itself under them.
 */
export function addFavourite(options: FavouriteOptions, nowMs: number): boolean {
  if (options.sensitive || !options.reopenable) return false;
  const existing = readAll();
  if (existing.some((f) => f.handle === options.handle)) return true;
  if (existing.length >= MAX_ENTRIES) return false;

  writeAll([
    ...existing,
    {
      handle: options.handle,
      displayName: options.displayName,
      formatId: options.formatId,
      sizeBytes: options.sizeBytes,
      addedAtMs: nowMs,
    },
  ]);
  return true;
}

export function removeFavourite(handle: string): void {
  writeAll(readAll().filter((f) => f.handle !== handle));
}

/** Pin if unpinned, unpin if pinned. Returns the resulting pinned state. */
export function toggleFavourite(options: FavouriteOptions, nowMs: number): boolean {
  if (isFavourite(options.handle)) {
    removeFavourite(options.handle);
    return false;
  }
  return addFavourite(options, nowMs);
}

export function isFavourite(handle: string): boolean {
  return readAll().some((f) => f.handle === handle);
}

/** Every favourite, most recently pinned first. Not yet checked on disk. */
export function favouriteFiles(): FavouriteFile[] {
  return readAll().sort((a, b) => b.addedAtMs - a.addedAtMs);
}

export function clearFavourites(): void {
  writeAll([]);
}

/**
 * Drop favourites whose file is gone.
 *
 * A pinned file that has since been deleted must disappear rather than sit
 * there failing on click — the same trade the recent list makes, for the same
 * reason.
 */
export async function pruneMissingFavourites(store: FileStore): Promise<FavouriteFile[]> {
  const entries = favouriteFiles();
  const alive: FavouriteFile[] = [];

  for (const entry of entries) {
    try {
      const stat = await store.stat(entry.handle);
      if (stat.exists) alive.push(entry);
    } catch {
      // A handle the store cannot even stat is not one it can open.
    }
  }

  if (alive.length !== entries.length) writeAll(alive);
  return alive;
}
